import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useRiskScoringForm } from "../../riskScoring/useRiskScoringForm";
import { useLinkedControls } from "../../riskControls/RiskControls.hooks";
import { SelectableRiskScenario } from "../../riskAssessmentsOverview/addRiskScenarioDialog/AddRiskScenarioDialog.types";
import { entityKey, entityTypeLabel } from "../RiskOperationalOverview.types";
import { IBulkAssessDialog } from "./BulkAssessDialog";
import { BulkAssessEntityRow, BulkAssessRequest, BulkAssessResult, BulkAssessScenarioRow } from "./BulkAssessDialog.types";

export function useBulkAssessDialog(props: IBulkAssessDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const scoringForm = useRiskScoringForm(props.riskAreaId);

    // The table selection is the single source of truth: the dialog renders props.entities live and removing
    // here calls back to the overview, which unchecks the row and re-renders the dialog with one fewer entity.
    const entities = props.entities;
    const [selectedScenarioIds, setSelectedScenarioIds] = useState<Array<string>>([]);
    const [scenarioError, setScenarioError] = useState("");
    const [frameworkControlIds, setFrameworkControlIds] = useState<Array<string>>([]);

    // Create-only: no pre-existing links, so the section shows purely on whether anything is pickable.
    const { availableControls, showLinkedControls } = useLinkedControls([]);

    const distinctEntityTypes = Array.from(new Set(entities.map((entity) => entity.type)));
    const entityTypesQueryString = distinctEntityTypes.map((type) => `entityTypes=${type}`).join("&");
    const scenariosQuery = useDotLegalQuery<Array<SelectableRiskScenario>>(
        `/risk-assessments/${props.riskAreaId}/applicable-scenarios?${entityTypesQueryString}`,
        { enabled: distinctEntityTypes.length > 0 }
    );
    const scenarioOptions = (scenariosQuery.data ?? []).map((scenario) => ({ id: scenario.id, name: scenario.name }));
    const scenarioNameById = new Map((scenariosQuery.data ?? []).map((scenario) => [scenario.id, scenario.name]));

    // Single-select picker: already-chosen scenarios drop out of the options so they can't be added twice.
    const availableScenarioOptions = scenarioOptions.filter((option) => !selectedScenarioIds.includes(option.id));

    const isValid = scoringForm.isValid && selectedScenarioIds.length > 0 && entities.length > 0;

    const entityRows: Array<BulkAssessEntityRow> = entities.map((entity) => ({
        key: entityKey(entity.type, entity.entityId),
        type: entity.type,
        typeLabel: entityTypeLabel(entity.type, translateString),
        name: entity.name,
    }));

    const removeEntity = (key: string) => props.onRemoveEntity(key);

    const selectedScenarioRows: Array<BulkAssessScenarioRow> = selectedScenarioIds
        .map((id) => ({ id, name: scenarioNameById.get(id) ?? "" }))
        .filter((row) => row.name !== "");

    const setSelectedScenarioIdsAndClearError = (value: Array<string>) => {
        setSelectedScenarioIds(value);
        setScenarioError(value.length === 0 ? translateString("fieldRequired") : "");
    };

    const addScenario = (id: string) => setSelectedScenarioIdsAndClearError([...selectedScenarioIds, id]);

    const removeScenario = (id: string) => setSelectedScenarioIdsAndClearError(selectedScenarioIds.filter((scenarioId) => scenarioId !== id));

    const saveMutation = useDotLegalMutation(
        (request: BulkAssessRequest) => post<BulkAssessResult>(`/risk-assessments/${props.riskAreaId}/bulk-assess`, request),
        {
            onSuccess: (response: Result<BulkAssessResult>) => {
                if (response.success()) {
                    snackbar.show(translateString("bulkAssessCreated", { count: response.value().created }));
                    props.onSaved();
                    props.onClose();
                }
            },
        }
    );

    const validateAndSave = () => {
        const isScoringValid = scoringForm.validate();
        const hasScenarios = selectedScenarioIds.length > 0;
        setScenarioError(hasScenarios ? "" : translateString("fieldRequired"));

        if (!isScoringValid || !hasScenarios || entities.length === 0) return;

        saveMutation.mutateAsync({
            entities: entities.map((entity) => ({ entityType: entity.type, entityId: entity.entityId })),
            scenarioIds: selectedScenarioIds,
            consequence: scoringForm.consequence!,
            probability: scoringForm.probability!,
            consequenceJustification: scoringForm.consequenceJustification,
            probabilityJustification: scoringForm.probabilityJustification,
            consequenceTypeIds: scoringForm.selectedConsequenceTypeIds,
            measureIds: scoringForm.selectedMeasureIds,
            exposureValue: scoringForm.exposureValue,
            confidence: scoringForm.confidence,
            participantIds: scoringForm.selectedParticipantIds,
            frameworkControlIds,
        });
    };

    return {
        scoringForm,
        isLoading: scoringForm.isLoading,
        isValid,
        entities,
        entityRows,
        removeEntity,
        availableScenarioOptions,
        addScenario,
        selectedScenarioRows,
        removeScenario,
        scenarioError,
        validateAndSave,
        inProgress: saveMutation.isLoading,
        showLinkedControls,
        availableControls,
        frameworkControlIds,
        setFrameworkControlIds,
    };
}
